import {Injectable, OnDestroy} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
  AppState,
  RouterStateUrl,
  selectMicrositeSessions,
  selectRouter,
  track
} from '../../core.state';

import {Store} from '@ngrx/store';
import {catchError, exhaustMap, filter, map, switchMap, takeUntil, withLatestFrom} from "rxjs/operators";

import {EMPTY, of, Subject} from "rxjs";
import {SyndicateClient} from "../../../client/syndicate";
import {
  acceptOffer,
  successAcceptOffer,
  failSyndicateOffer,
  callSyndicate,
  createSyndicate,
  failSyndicate,
  joinSyndicate,
  setSyndicatePublic,
  setSyndicateVoidToSolo,
  successCreateSyndicate,
  successJoinSyndicate,
  successSyndicate,
  successSyndicatePublic,
  successSyndicateVoidToSolo,
  successUpdateSyndicate,
  updateListItem,
  updateSyndicate, callList, loadComments
} from "../syndicates.actions";
import { selectSyndicate} from "../syndicates.selectors";
import {ws} from "../syndicates.reducer";
import * as _ from 'lodash-es';
import {SyndicatesScope} from "../syndicates.model";
import {setWebsocketEvent} from '../../coreWebsockets/coreWebsockets.actions';
import {routerNavigationAction} from '@ngrx/router-store';
import {callPool, partialUpdate} from '../../pool/pool.actions';
import {callSoloTicket, callSyndicateTicket} from '../../tickets/tickets.actions';
import {selectTicketState} from '../../tickets/tickets.selector';
import {CommunicationHandlerService} from "../../../services/utils/communication-handler/communication-handler.service";

@Injectable()
export class SyndicateSoloEffects implements OnDestroy {
  private readonly destroy$ = new Subject<void>();


  routerNavigationSyndicateItem = createEffect(() => {
    return this.actions$.pipe(
      ofType(routerNavigationAction),
      filter((routerProps) => !(routerProps.payload.routerState as unknown as RouterStateUrl).wallet),
      filter(propVal => propVal.payload.event.url.includes('/syndicates/join/pool')),
      takeUntil(this.destroy$),
      map(propVal => {
        return {
          pool_id: (propVal.payload.routerState as unknown as RouterStateUrl)?.params?.pool_id,
          syndicate_id: (propVal.payload.routerState as unknown as RouterStateUrl)?.params?.syndicate_id
        }
      }),
      filter(params => !!params.pool_id && !!params.syndicate_id),
      switchMap((routerProps) => {
        return [
          callPool({id: routerProps.pool_id}),
          callSyndicate({id: routerProps.syndicate_id})
        ]
      }))
  }, {dispatch: true});


  getSyndicate = createEffect(() => {
    return this.actions$.pipe(
      ofType(callSyndicate),
      takeUntil(this.destroy$),
      // Use exhaustMap to prevent duplicate concurrent requests
      exhaustMap((propVal) => {
        return this.syndicatesClient.get(propVal.id).pipe(
          map((data: SyndicatesScope.Syndicate) => {
            // Dispatch side effects separately for clarity
            this.store.dispatch(updateListItem({syndicate: data}));
            this.store.dispatch(callSyndicateTicket({
              portion_id: Number(data.stringified_id)
            }));
            return successSyndicate({portion: data});
          }),
          catchError(error => {
            console.error('[Syndicate Solo Effects] Failed to load syndicate:', error);
            return of(failSyndicate({error: error}))
          })
        )
      })
    )
  }, {dispatch: true});

  createSyndicate = createEffect(() => {
    return this.actions$.pipe(
      ofType(createSyndicate),
      takeUntil(this.destroy$),
      // Use exhaustMap to prevent duplicate syndicate creation
      exhaustMap((propVal) => {
        return this.syndicatesClient.create({syndicate: propVal.model}).pipe(
          map(data => {
            // Dispatch side effects
            this.store.dispatch(setWebsocketEvent({
              event: 'BET_SUCCESS',
              data: data
            }));
            this.store.dispatch(callList({}));
            // Pre-existing assumption: model is populated whenever this action fires — not independently verified
            return successCreateSyndicate({response: Object.assign({}, data, {is_public: propVal.model!.is_public})});
          }),
          catchError(error => {
            console.error('[Syndicate Solo Effects] Failed to create syndicate:', error);
            
            // Handle specific error codes for geo-compliance
            const errorCodesToHandle = [
              'ERR_GEO_COMPLY_VALIDATION',
              'ERR_MAXIMUM_DAILY_AMOUNT_EXCEEDED',
              'ERR_MAXIMUM_WEEKLY_AMOUNT_EXCEEDED',
              'ERR_MAXIMUM_MONTHLY_AMOUNT_EXCEEDED'
            ];

            if (errorCodesToHandle.includes(error.code)) {
              const message = error.code === 'ERR_GEO_COMPLY_VALIDATION'
                ? 'process_geo_validation'
                : error.code;
              this.communicationHandler.sendMessage({message});
            }
            
            this.store.dispatch(setWebsocketEvent({
              event: 'BET_ERROR',
              data: error
            }));
            return of(failSyndicate({error: error}))
          })
        )
      })
    )
  });

  ///
  trackSuccessCreateSyndicate = createEffect(() => {
    return this.actions$.pipe(
      ofType(successCreateSyndicate),
      takeUntil(this.destroy$),
      exhaustMap((propVal) => {
        const event = {
          event: 'micrositeEvent',
          eventCategory: 'bet',
          eventAction: 'microsite',
          eventLabel: 'create',
          eventValue: propVal.response.first_portion_id
        }

        return of(track({event}))
      })
    )
  });

  updateSyndicate = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateSyndicate),
      takeUntil(this.destroy$),
      switchMap((propVal) => {
        return this.syndicatesClient.updateSyndicate(String(propVal.id), propVal.model).pipe(
          switchMap(data => {
            return [
              updateListItem({syndicate: data as any}),
              successUpdateSyndicate({response: data as any}),
            ]
          }),
          catchError(error => {
            return of(failSyndicate({error: error}))
          })
        )
      })
    )
  });

  joinSyndicate = createEffect(() => {
    return this.actions$.pipe(
      ofType(joinSyndicate),
      takeUntil(this.destroy$),
      switchMap((propVal) => {
        // Pre-existing assumption: model is populated whenever this action fires — not independently verified
        return this.syndicatesClient.join(propVal.id, propVal.model!).pipe(
          switchMap(data => {
            return [
              // Do not remove this comment
              // callMicrositeSessions(),
              successJoinSyndicate({response: data}),
              callSyndicate({id: Number(propVal.id)})
            ]
          }),
          catchError(error => {
            // Mido only
            const errorCodesToHandle = [
              'ERR_GEO_COMPLY_VALIDATION',
              'ERR_MAXIMUM_DAILY_AMOUNT_EXCEEDED',
              'ERR_MAXIMUM_WEEKLY_AMOUNT_EXCEEDED',
              'ERR_MAXIMUM_MONTHLY_AMOUNT_EXCEEDED'
            ];

            if (errorCodesToHandle.includes(error.code)) {
              // For 'ERR_GEO_COMPLY_VALIDATION', use a custom message, otherwise use the error code as message
              const message = error.code === 'ERR_GEO_COMPLY_VALIDATION'
                ? 'process_geo_validation'
                : error.code;

              this.communicationHandler.sendMessage({message});
            }
            this.store.dispatch(setWebsocketEvent({
              event: 'BET_ERROR',
              data: error
            }));
            return of(failSyndicate({error: error}))
          })
        )
      })
    )
  });

  successJoinSyndicate = createEffect(() => {
    return this.actions$.pipe(
      ofType(successJoinSyndicate),
      withLatestFrom(this.store.select(selectSyndicate)),
      takeUntil(this.destroy$),
      exhaustMap(([propVal, currentSyndicate]) => {
        if (!currentSyndicate) {
          // Because it is getting called multiple times (with currentSyndicate and after we clear it)
          return EMPTY;
        }

        const betSuccess: any = _.cloneDeep(propVal.response);

        this.store.dispatch(setWebsocketEvent({
          event: 'BET_SUCCESS',
          data: betSuccess
        }));

        betSuccess['syndicate'].stringified_id = currentSyndicate.stringified_id;
        const event = {
          event: 'micrositeEvent',
          eventCategory: 'bet',
          eventAction: 'microsite',
          eventLabel: 'join',
          eventValue: betSuccess.id
        };

        return of(track({event: event}));
      })
    )
  }, {dispatch: true});

  setSyndicatePublic = createEffect(() => {
    return this.actions$.pipe(
      ofType(setSyndicatePublic),
      withLatestFrom(this.store.select(selectSyndicate), this.store.select(selectRouter), this.store.select(selectTicketState)),
      takeUntil(this.destroy$),
      switchMap(([propVal, currentSyndicate, routerState, ticketsState]) => {
        return this.syndicatesClient.updateSyndicate(propVal.id.toString(), propVal.model).pipe(
          switchMap((data: any) => {
            const actions: any[] = [
              successSyndicatePublic({response: data as any}),
              successSyndicate({portion: Object.assign(_.cloneDeep(currentSyndicate || data), {is_public: true})}),
              updateListItem({syndicate: Object.assign(_.cloneDeep(currentSyndicate || data), {is_public: true})}),
            ];
            if (routerState && routerState.state.url.includes('/tickets')) {
              const currentTicket = ticketsState?.current_ticket;
              const isSyndicate = !!currentTicket?.syndicate;

              if (isSyndicate) {
                actions.push(callSyndicateTicket({
                  portion_id: Number(currentTicket!.syndicate!.id) // isSyndicate true guarantees currentTicket and currentTicket.syndicate are both defined (optional-chained truthy check above)
                }))
              } else {
                actions.push(callSoloTicket({
                  // Pre-existing assumption (unchanged): current_ticket is populated whenever routerState is on a /tickets route.
                  ticket_id: currentTicket!.stringified_id
                }))
              }
            }

            return actions;
          }),
          catchError(error => {
            return of(failSyndicate({error: error}))
          })
        )
      })
    )
  }, {dispatch: true});

  trackSuccessSyndicatePublic = createEffect(() => {
    return this.actions$.pipe(
      ofType(successSyndicatePublic),
      withLatestFrom(this.store.select(selectSyndicate)),
      takeUntil(this.destroy$),
      exhaustMap(([propVal, currentSyndicate]) => {
        const event = {
          event: 'micrositeEvent',
          eventCategory: 'Syndicates',
          eventAction: 'Click',
          eventLabel: 'Public_Status',
          eventValue: currentSyndicate?.id || propVal.response.id
        }

        return of(track({event: event}));
      })
    )
  }, {dispatch: true});

  setSyndicateVoidToSolo = createEffect(() => {
    return this.actions$.pipe(
      ofType(setSyndicateVoidToSolo),
      withLatestFrom(this.store.select(selectSyndicate), this.store.select(selectRouter), this.store.select(selectTicketState)),
      takeUntil(this.destroy$),
      switchMap(([propVal, currentSyndicate, routerState, ticketsState]) => {
        return this.syndicatesClient.updateSyndicate(String(propVal.id), propVal.model).pipe(
          switchMap((data: any) => {
            const actions: any[] = [
              successSyndicateVoidToSolo({response: data as any, model: propVal.model}),
              updateListItem({syndicate: Object.assign({}, (currentSyndicate || data), {void_to_solo: propVal.model.void_to_solo})}),
              successSyndicate({portion: (<SyndicatesScope.Syndicate>{void_to_solo: propVal.model.void_to_solo})})
            ];

            if (routerState && routerState.state.url.includes('/tickets')) {
              const currentTicket = ticketsState?.current_ticket;
              const isSyndicate = !!currentTicket?.syndicate;

              if (isSyndicate) {
                actions.push(callSyndicateTicket({
                  portion_id: Number(currentTicket!.syndicate!.id) // isSyndicate true guarantees currentTicket and currentTicket.syndicate are both defined (optional-chained truthy check above)
                }))
              } else {
                actions.push(callSoloTicket({
                  // Pre-existing assumption (unchanged): current_ticket is populated whenever routerState is on a /tickets route.
                  ticket_id: currentTicket!.stringified_id
                }))
              }
            }

            return actions;
          }),
          catchError(error => {
            return of(failSyndicate({error: error}))
          })
        )
      })
    )
  }, {dispatch: true});

  trackSuccessSyndicateVoidToSolo = createEffect(() => {
    return this.actions$.pipe(
      ofType(successSyndicateVoidToSolo),
      withLatestFrom(this.store.select(selectSyndicate)),
      takeUntil(this.destroy$),
      exhaustMap(([propVal, currentSyndicate]) => {
        const eventValue = {
          void_to_solo: propVal.model.void_to_solo,
          syndicate_id: currentSyndicate?.id || propVal.response.id
        }

        const event = {
          event: 'micrositeEvent',
          eventCategory: 'Syndicates',
          eventAction: 'Click',
          eventLabel: 'Void_To_Solo',
          eventValue: encodeURIComponent(JSON.stringify(eventValue))
        }

        return of(track({event: event}));
      })
    )
  }, {dispatch: true});



  acceptOffer = createEffect(() => {
    return this.actions$.pipe(
      ofType(acceptOffer),
      takeUntil(this.destroy$),
      exhaustMap((model: any) => {
        return this.syndicatesClient.acceptOffer(model.id, model.payload).pipe(
          map((data: any) => {
            return successAcceptOffer({response: data})
          }),
          catchError(error => {
            return of(failSyndicateOffer({error: error}))
          })
        )
      })
    )
  });

  successSyndicate = createEffect(() => {
    return this.actions$.pipe(
      ofType(successSyndicate),
      filter(propVal => !!propVal.portion?.id),
      takeUntil(this.destroy$),
      exhaustMap((propVal) => {
        if (propVal?.portion?.customer_portion && propVal.portion.customer_portion !== '0.0') {
          return of(loadComments({
            params: {
              page: 1,
              model_id: String(propVal.portion.id),
              feed_type: 'syndicate'
            }
          }))
        }
        return EMPTY;
      })
    )
  }, {dispatch: true});


  ///// LIVE UPDATE
  setWebsocketEvent = createEffect(() => {
    return this.actions$.pipe(
      ofType(setWebsocketEvent),
      filter(propVal => ws.RELOAD_EVENTS.concat(Object.keys(ws)).includes(propVal.event)),
      withLatestFrom(this.store.select(selectSyndicate), this.store.select(selectMicrositeSessions)),
      takeUntil(this.destroy$),
      switchMap(([propVal, portion, micrositeSessions]) => {
        if (ws.RELOAD_EVENTS.includes(propVal.event)) {
          return [
            callList({}),
            (portion?.stringified_id ? callSyndicate({id: Number(portion?.stringified_id)}) : null),
          ].filter(action => !!action);
        }

        if (
          !!propVal.data &&
          micrositeSessions.ext_id &&
          (micrositeSessions.ext_id === propVal.data.customer_id) &&
          String(portion?.id) === String(propVal.data.model_id)
        ) {
          return of(partialUpdate({
            data: propVal.data,
            event: propVal.event
          }))
        }

        return EMPTY
      })
    )
  }, {dispatch: true});

  constructor(
    public actions$: Actions,
    public store: Store<AppState>,
    public syndicatesClient: SyndicateClient,
    public communicationHandler: CommunicationHandlerService
  ) {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
